import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Segmented, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { loadGoogleMaps } from '../../utils/googleMaps';
import { NEGATIF_RENK, htmlKacir, para, sayi, useAktifYukle } from './biOrtak';

const { Text } = Typography;

// MapContainer.jsx karanlık stilinin kısaltılmışı
const KARANLIK = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
];
const MERKEZ = { lat: 39.0, lng: 35.2 };

const SEVIYELER = [
  { value: 'lokasyon', label: 'Lokasyon' },
  { value: 'ilce', label: 'İlçe' },
  { value: 'il', label: 'İl' },
];

/**
 * Harita sekmesi: /bi-v2/{key}/harita — nokta başına Σ ölçü. Daire yarıçapı √(|tutar| / max) ölçeğinde,
 * piksel cinsinden (yakınlaştırmadan bağımsız). Negatif toplamlar mor. İl / ilçe noktası, gruptaki
 * lokasyon koordinatlarının ortalamasıdır.
 */
export default function BiHarita({ api, rapor, params, olcu, aktif, surum, renk, darkMode }) {
  const kutuRef = useRef(null);
  const haritaRef = useRef(null);
  const pencereRef = useRef(null);
  const isaretRef = useRef([]);
  const [haritaHazir, setHaritaHazir] = useState(false);
  const [haritaHata, setHaritaHata] = useState(null);
  const [seviye, setSeviye] = useState('lokasyon');

  const istek = useMemo(() => ({ seviye, olcu, ...params }), [seviye, olcu, params]);
  const anahtar = rapor ? JSON.stringify([rapor.key, istek, surum]) : null;
  const { veri, yukleniyor, hata: hataMetni, yeniden } = useAktifYukle(aktif, anahtar,
    () => api.rapor(rapor.key, 'harita', istek));

  // SDK + harita (bir kez; sekme ilk açıldığında)
  useEffect(() => {
    if (!aktif || haritaRef.current) return undefined;
    let iptal = false;
    loadGoogleMaps()
      .then((maps) => {
        if (iptal || !kutuRef.current || haritaRef.current) return;
        haritaRef.current = new maps.Map(kutuRef.current, {
          center: MERKEZ, zoom: 6, styles: darkMode ? KARANLIK : [], mapTypeControl: true,
          streetViewControl: false, fullscreenControl: true, gestureHandling: 'greedy',
        });
        pencereRef.current = new maps.InfoWindow();
        setHaritaHazir(true);
      })
      .catch((e) => { if (!iptal) setHaritaHata(e?.message || 'Google Maps yüklenemedi.'); });
    return () => { iptal = true; };
  }, [aktif]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (haritaRef.current) haritaRef.current.setOptions({ styles: darkMode ? KARANLIK : [] });
  }, [darkMode]);

  // Noktalar
  useEffect(() => {
    const maps = window.google?.maps;
    const harita = haritaRef.current;
    if (!haritaHazir || !maps || !harita) return;
    isaretRef.current.forEach((m) => m.setMap(null));
    isaretRef.current = [];
    if (pencereRef.current) pencereRef.current.close();
    const noktalar = veri?.noktalar || [];
    if (!noktalar.length) return;
    const enBuyuk = Math.max(...noktalar.map((p) => Math.abs(p.tutar))) || 1;
    const sinir = new maps.LatLngBounds();
    const lokasyonSeviyesi = veri.seviye === 'lokasyon';
    noktalar.forEach((p) => {
      const oran = Math.abs(p.tutar) / enBuyuk;
      const konum = { lat: p.enlem, lng: p.boylam };
      const m = new maps.Marker({
        position: konum, map: harita, title: `${p.ad || ''} — ${para(p.tutar)}`,
        zIndex: Math.round(1000 * (1 - oran)), // küçük daireler üstte kalsın, tıklanabilsin
        icon: {
          path: maps.SymbolPath.CIRCLE, scale: 3 + 22 * Math.sqrt(oran),
          fillColor: p.tutar < 0 ? NEGATIF_RENK : renk, fillOpacity: 0.55, strokeColor: '#ffffff', strokeWeight: 1,
        },
      });
      m.addListener('click', () => {
        const no = lokasyonSeviyesi ? p.anahtar?.[0] : null;
        pencereRef.current.setContent(
          `<div style="min-width:200px;color:#1f1f1f;font-size:13px;line-height:1.5">
            <div style="font-weight:600;margin-bottom:4px">${htmlKacir(p.ad || '(adsız)')}</div>
            ${no ? `<div>Lokasyon No: <a href="/detay/${encodeURIComponent(no)}" target="_blank" rel="noreferrer">${htmlKacir(no)}</a></div>` : ''}
            <div>${htmlKacir(olcu)}: <b>${htmlKacir(para(p.tutar))}</b></div>
            <div>Adet: ${htmlKacir(sayi(p.adet))} · Lokasyon: ${htmlKacir(sayi(p.lokasyon))}</div>
          </div>`);
        pencereRef.current.open({ anchor: m, map: harita });
      });
      isaretRef.current.push(m);
      sinir.extend(konum);
    });
    if (noktalar.length === 1) {
      harita.setCenter({ lat: noktalar[0].enlem, lng: noktalar[0].boylam });
      harita.setZoom(12);
    } else {
      harita.fitBounds(sinir);
    }
  }, [haritaHazir, veri, renk, olcu]);

  useEffect(() => () => { isaretRef.current.forEach((m) => m.setMap(null)); isaretRef.current = []; }, []);

  const noktalar = veri?.noktalar || [];
  const haritadaki = noktalar.reduce((t, p) => t + (p.tutar || 0), 0);

  return (
    <div>
      <Space wrap style={{ marginBottom: 10 }}>
        <Segmented value={seviye} onChange={setSeviye} options={SEVIYELER} />
        <Button icon={<ReloadOutlined />} onClick={yeniden} loading={yukleniyor}>Yeniden yükle</Button>
        {veri && (
          <>
            <Tag>{sayi(noktalar.length)} nokta</Tag>
            <Tag color="processing">Haritada: {para(haritadaki)}</Tag>
            <Tooltip title="Enlem/boylamı olmayan (lokasyonsuz, proje geneli ya da koordinatı geçersiz lokasyon) satırların toplamı; haritada gösterilmez.">
              <Tag color={veri.koordinatsiz_tutar ? 'warning' : 'default'}>Koordinatsız: {para(veri.koordinatsiz_tutar)}</Tag>
            </Tooltip>
            {noktalar.length >= 5000 && <Tag color="warning">İlk 5.000 nokta</Tag>}
          </>
        )}
        <Tooltip title="Daire alanı |tutar| ile orantılı (√ ölçek). Mor daire: negatif toplam (iade / ters kayıt). İl / ilçe noktası gruptaki lokasyon koordinatlarının ortalamasıdır.">
          <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
        </Tooltip>
      </Space>
      {hataMetni && <Alert type="error" showIcon style={{ marginBottom: 10 }} message="Harita verisi alınamadı" description={hataMetni} />}
      {haritaHata && <Alert type="error" showIcon style={{ marginBottom: 10 }} message={haritaHata} />}
      <div className="bi-harita-sarmal">
        <div ref={kutuRef} className="bi-harita" />
        {(yukleniyor || (!haritaHazir && !haritaHata)) && (
          <div className="bi-harita-yukleniyor"><Spin tip={yukleniyor ? 'Veri yükleniyor…' : 'Harita yükleniyor…'}><div style={{ padding: 40 }} /></Spin></div>
        )}
      </div>
      {veri && !noktalar.length && !yukleniyor && (
        <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>Seçili süzgeçlerle koordinatlı nokta yok.</Text>
      )}
    </div>
  );
}
